import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Link,
} from "@mui/material";
import { Dropdown } from "../../../HelperFunctions/PayrollDropdown";
import { getCorporations } from "../../../api";
import PayrollsPopup from "../../../Components/PayrollPopup";
import { useAlerts } from "../../../Hooks";
import { AUTOMATION_TYPES } from "../../../Enums/Enum";

// --- Constants ---
const AUTOMATION_OPTIONS = [
  { id: "", label: "--Select--" },
  { id: AUTOMATION_TYPES.EMAIL, label: "Import/EMAIL" },
  { id: AUTOMATION_TYPES.API, label: "API" },
];

const COMPACT_DROPDOWN_SX = {
  "& .MuiFilledInput-root": {
    height: "28px",
    minHeight: "28px",
    backgroundColor: "white",
    paddingTop: 0,
    paddingBottom: 0,
    "&:hover:not(.Mui-disabled)": { backgroundColor: "white" },
    "&.Mui-focused": { backgroundColor: "white" },
  },
  "& .MuiAutocomplete-input": {
    padding: "0px 8px !important",
    fontSize: "0.7rem",
    height: "20px",
  },
};

/**
 * Individual Row Component in the By-Corporation preferences table.
 * Memoized to prevent re-rendering unaffected rows when an input changes.
 */
const CorporationTableRow = memo(({
  row,
  corporations,
  loadingCorps,
  payrollVendorTypes,
  onRowChange,
  onDeleteRow,
  onOpenApiModal,
}) => {
  const selectedCorp = corporations.find((c) => c.ID === row.corpId) || null;
  const selectedAutomation =
    AUTOMATION_OPTIONS.find((a) => a.id === row.automationType) || AUTOMATION_OPTIONS[0];
  const selectedVendor = Array.isArray(payrollVendorTypes)
    ? payrollVendorTypes.find((v) => v.ID === row.payrollName) || payrollVendorTypes[0] || null
    : null;

  return (
    <TableRow>
      {/* Corporation Legal Name */}
      <TableCell>
        <Dropdown
          name={`legalName-${row.id}`}
          value={selectedCorp}
          options={corporations}
          loading={loadingCorps}
          getOptionLabel={(option) => option?.LegalName || ""}
          isEqual={(option, value) => String(option?.ID) === String(value?.ID)}
          onChange={(name, newValue) => onRowChange(row.id, "legalName", newValue?.LegalName || "")}
          disabled={Boolean(row.detailId)}
          sx={COMPACT_DROPDOWN_SX}
        />
      </TableCell>

      {/* Corporation DBA (Read-only) */}
      <TableCell>
        <Typography fontSize="0.7rem" sx={{ px: 1 }}>{row.dba || "-"}</Typography>
      </TableCell>

      {/* Data Automation Type */}
      <TableCell>
        <Dropdown
          name={`automationType-${row.id}`}
          value={selectedAutomation}
          options={AUTOMATION_OPTIONS}
          getOptionLabel={(option) => option?.label || ""}
          isEqual={(option, value) => String(option?.id) === String(value?.id)}
          onChange={(name, newValue) => onRowChange(row.id, "automationType", newValue?.id || "")}
          sx={COMPACT_DROPDOWN_SX}
        />
      </TableCell>

      {/* Payroll Vendor Name */}
      <TableCell>
        <Dropdown
          name={`payrollName-${row.id}`}
          value={selectedVendor}
          options={Array.isArray(payrollVendorTypes) ? payrollVendorTypes : []}
          getOptionLabel={(option) => option?.Value || ""}
          isEqual={(option, value) => String(option?.ID) === String(value?.ID)}
          onChange={(name, newValue) => onRowChange(row.id, "payrollName", newValue?.ID || "")}
          sx={COMPACT_DROPDOWN_SX}
        />
      </TableCell>

      {/* API or Email Details */}
      <TableCell>
        {row.automationType === AUTOMATION_TYPES.API ? (
          <Link
            component="button"
            variant="body2"
            onClick={() => onOpenApiModal(row.id)}
            sx={{ fontWeight: 600, fontSize: "0.7rem", textDecoration: "underline", px: 1 }}
          >
            {row.apiConfigured ? "Configured" : "SET"}
          </Link>
        ) : row.automationType === AUTOMATION_TYPES.EMAIL ? (
          <Typography fontSize="0.7rem" sx={{ px: 1 }}>{row.apiOrEmail || "-"}</Typography>
        ) : (
          <Typography fontSize="0.7rem" sx={{ px: 1, color: "text.secondary" }}>-</Typography>
        )}
      </TableCell>

      {/* Actions (Delete Row) */}
      <TableCell align="center" sx={{ minWidth: "80px !important", width: "80px" }}>
        <Button
          size="small"
          aria-label={`Delete preference row for ${row.legalName || "corporation"}`}
          onClick={() => onDeleteRow(row.id)}
          sx={{
            fontSize: "0.7rem",
            color: "error.main",
            fontWeight: 600,
            textTransform: "uppercase",
            minWidth: 0,
            p: 0.5,
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
              boxShadow: "none",
            },
          }}
        >
          DELETE
        </Button>
      </TableCell>
    </TableRow>
  );
});

/**
 * By-Corporation Preference Table.
 * Renders multiple corporation rows with dynamic vendor options received from parent.
 */
const IsByCorporationTable = ({
  rows,
  setRows,
  onOpenApiModal,
  payrollVendorTypes = [],
}) => {
  const [corporations, setCorporations] = useState([]);
  const [loadingCorps, setLoadingCorps] = useState(false);
  const tableContainerRef = useRef(null);
  const shouldScrollToBottomRef = useRef(false);
  const { popup: alertPopup, showDeleteConfirm, showErrorPopup, hidePopup } = useAlerts();

  // Fetch Corporations once on mount
  useEffect(() => {
    const fetchCorps = async () => {
      setLoadingCorps(true);
      try {
        const data = await getCorporations();
        const formatted = (data || []).map((c) => ({
          LegalName: c.LegalName || "",
          ID: c.ID || "",
          Name: c.Name || "",
        }));
        setCorporations([{ LegalName: "--Select--", ID: "", Name: "" }, ...formatted]);
      } catch (error) {
        console.error("Error fetching corporations:", error);
      } finally {
        setLoadingCorps(false);
      }
    };
    fetchCorps();
  }, []);

  // --- Add New Preference Row ---
  const handleAddRow = useCallback(() => {
    shouldScrollToBottomRef.current = true;
    setRows((prevRows) => [
      ...prevRows,
      {
        id: Date.now(),
        detailId: "",
        legalName: "",
        dba: "",
        automationType: "",
        payrollName: "",
        apiOrEmail: "",
        corpId: "",
        apiKey: "",
        securityKey: "",
        apiConfigured: false,
      },
    ]);
  }, [setRows]);

  // Auto-scroll to newly added empty row
  useEffect(() => {
    if (shouldScrollToBottomRef.current && tableContainerRef.current) {
      shouldScrollToBottomRef.current = false;
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTo({
            top: tableContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 50);
    }
  }, [rows.length]);

  // --- Delete Preference Row ---
  const handleDeleteRow = useCallback((id) => {
    const rowToDelete = rows.find((row) => row.id === id);
    const deleteAction = () => setRows((prevRows) => prevRows.filter((row) => row.id !== id));

    if (rowToDelete && rowToDelete.corpId) {
      showDeleteConfirm("Do you want to delete the row?", () => {
        deleteAction();
      });
    } else {
      deleteAction();
    }
  }, [rows, setRows, showDeleteConfirm]);

  // --- Change Preference Field ---
  const handleRowChange = useCallback((id, field, value) => {
    // When corporation legal name changes, prevent duplicate corporation assignments across rows
    if (field === "legalName") {
      const selectedCorp = corporations.find((c) => c.LegalName === value);
      const newCorpId = selectedCorp?.ID || "";

      if (newCorpId && rows.some((r) => r.id !== id && r.corpId === newCorpId)) {
        showErrorPopup("Duplicate corporation selected. Please select a unique corporation for each row.");
        return; // Revert change / ignore duplicate selection
      }
    }

    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };

          // When corporation legal name changes, auto-populate DBA & Corp ID
          if (field === "legalName") {
            const selectedCorp = corporations.find((c) => c.LegalName === value);
            updatedRow.dba = selectedCorp?.Name || "";
            updatedRow.corpId = selectedCorp?.ID || "";

            if (selectedCorp?.ID) {
              // Default automation type to EMAIL on corporation selection
              updatedRow.automationType = AUTOMATION_TYPES.EMAIL;
            } else {
              // Reset dependent vendor and credential fields on deselect
              updatedRow.automationType = "";
              updatedRow.payrollName = "";
              updatedRow.apiOrEmail = "";
              updatedRow.apiKey = "";
              updatedRow.securityKey = "";
              updatedRow.apiConfigured = false;
            }
          }

          return updatedRow;
        }
        return row;
      })
    );
  }, [corporations, rows, setRows, showErrorPopup]);

  return (
    <>
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          maxHeight: "65vh",
          overflowY: "auto",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            "& .MuiTableCell-root": {
              padding: "4px 8px",
              fontSize: "0.7rem",
              minWidth: 150,
              border: "1px solid #e0e0e0",
            },
          }}
        >
          <TableHead
            sx={{
              bgcolor: "#B5DCF2",
              "& .MuiTableCell-root": {
                bgcolor: "#B5DCF2 !important",
                color: "#000",
                fontWeight: 600,
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: "20%", color: "#000" }}>Corporation Legal Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "15%", color: "#000" }}>Corporation DBA</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "20%", color: "#000" }}>Data Automation type</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "15%", color: "#000" }}>Payroll Name</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "20%", color: "#000" }}>API or EMAIL Details</TableCell>
              <TableCell align="center" sx={{ minWidth: "80px !important", width: "80px", padding: "0 !important" }}>
                <Button
                  fullWidth
                  size="small"
                  aria-label="Add Row"
                  onClick={handleAddRow}
                  sx={{ whiteSpace: "nowrap", fontWeight: 600, color: "#000", fontSize: "0.7rem", height: "100%", borderRadius: 0 }}
                >
                  ADD ROW
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <CorporationTableRow
                key={row.id}
                row={row}
                corporations={corporations}
                loadingCorps={loadingCorps}
                payrollVendorTypes={payrollVendorTypes}
                onRowChange={handleRowChange}
                onDeleteRow={handleDeleteRow}
                onOpenApiModal={onOpenApiModal}
              />
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No rows added. Click "ADD ROW" to create one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PayrollsPopup
        open={alertPopup.open}
        popupType={alertPopup.type}
        message={alertPopup.message}
        onConfirm={alertPopup.onConfirm}
        onCancel={hidePopup}
      />
    </>
  );
};

export default IsByCorporationTable;
